"use client";

import {JSX, useState} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {useKeenSlider} from "keen-slider/react";

import {assetPath} from "@/shared/utils/asset-path";

import "./parts/styles/gallery-slider.scss";
import "keen-slider/keen-slider.min.css";

interface GallerySliderProps {
    images: Array<{src: string; alt?: string}>;
    className?: string;
}

export function GallerySlider({images, className = ""}: GallerySliderProps): JSX.Element | null {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isMainLoaded, setIsMainLoaded] = useState(false);
    const hasImages = images && images.length > 0;

    const [thumbRef, thumbSlider] = useKeenSlider<HTMLDivElement>(
        {
            initial: 0,
            loop: true,
            mode: "snap",
            rubberband: true,

            slides: {
                perView: "auto",
                spacing: 10,
            },

            breakpoints: {
                "(max-width: 767px)": {
                    slides: {
                        perView: "auto",
                        spacing: 10,
                    },
                },
            },
            created() {
                setIsLoaded(true);
            },
        },
        [],
    );
    const [sliderRef, slider] = useKeenSlider<HTMLDivElement>(
        {
            initial: 0,
            loop: true,
            animationEnded(s) {
                setActiveIndex(s.track.details.rel);
                thumbSlider.current?.moveToIdx(s.track.details.rel);
            },
            slides: {perView: 1},
            rubberband: true,
            created() {
                setIsMainLoaded(true);
            },
        },
        [],
    );

    const prev = (): void => slider.current?.prev();
    const next = (): void => slider.current?.next();
    if (!hasImages) return null;

    return (
        <div className={`ttd-gallery ${className}`}>
            <div className="ttd-gallery__main">
                {!isMainLoaded && <div className="ttd-gallery__main-skeleton" />}

                <button type="button" className="ttd-gallery__nav ttd-gallery__nav--prev" aria-label="Previous" onClick={prev}>
                    <SmartImage
                        src={assetPath("/images/things-to-do/icons/arrow-right.svg")}
                        alt="Prev"
                        width={24}
                        height={24}
                        className="ttd-gallery__nav-icon ttd-gallery__nav-icon--prev"
                    />
                </button>

                <div ref={sliderRef} className="keen-slider ttd-gallery__track" style={{opacity: isMainLoaded ? 1 : 0}}>
                    {images.map((img, index) => (
                        <div className="keen-slider__slide ttd-gallery__slide" key={`${img.src}-slide-${index}`}>
                            <div className="ttd-gallery__slide-inner">
                                <SmartImage
                                    src={img.src}
                                    alt={img.alt ?? ""}
                                    fill
                                    sizes="(max-width: 1024px) 100vw, 720px"
                                    className="ttd-gallery__slide-image"
                                    priority={index === 0}
                                />
                            </div>
                        </div>
                    ))}
                </div>

                <button type="button" className="ttd-gallery__nav ttd-gallery__nav--next" aria-label="Next" onClick={next}>
                    <SmartImage
                        src={assetPath("/images/things-to-do/icons/arrow-right.svg")}
                        alt="Next"
                        width={24}
                        height={24}
                        className="ttd-gallery__nav-icon"
                    />
                </button>
            </div>

            {images.length > 1 && (
                <>
                    {!isLoaded && (
                        <div className="ttd-gallery__thumbs-skeleton">
                            {Array.from({length: 14}).map((_, idx) => (
                                <div key={`skeleton-${idx}`} className="ttd-gallery__thumb-skeleton" />
                            ))}
                        </div>
                    )}
                    <div ref={thumbRef} className="ttd-gallery__thumbs keen-slider" style={{opacity: isLoaded ? 1 : 0}}>
                        {images.map((img, idx) => (
                            <div className="keen-slider__slide" key={`thumb-slide-${idx}-${img.src}`}>
                                <button
                                    type="button"
                                    className={`ttd-gallery__thumb ${idx === activeIndex ? "is-active" : ""}`}
                                    onClick={() => {
                                        slider.current?.moveToIdx(idx);
                                        setActiveIndex(idx);
                                        thumbSlider.current?.moveToIdx(idx);
                                    }}
                                    aria-label={`Show image ${idx + 1}`}
                                    aria-current={idx === activeIndex ? "true" : undefined}
                                >
                                    <SmartImage src={img.src} alt={img.alt ?? ""} width={60} height={60} className="ttd-gallery__thumb-image" />
                                </button>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
